import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  Search,
  Loader2,
  Filter,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { apiGet } from '../lib/api'

type Level = 'info' | 'warn' | 'error'

interface LogRow {
  id: string
  ts: string
  level: Level
  category: string
  userId: string | null
  userEmail: string | null
  ip: string | null
  action: string
  entity: string | null
  entityId: string | null
  message: string | null
  meta: string | null
}

interface LogsData {
  total: number
  page: number
  pageSize: number
  pages: number
  categories: string[]
  data: LogRow[]
}

function levelBadge(level: Level) {
  if (level === 'error') {
    return (
      <Badge variant="danger">
        <AlertCircle className="size-3" /> error
      </Badge>
    )
  }
  if (level === 'warn') {
    return (
      <Badge variant="warning">
        <AlertTriangle className="size-3" /> warn
      </Badge>
    )
  }
  return (
    <Badge variant="info">
      <Info className="size-3" /> info
    </Badge>
  )
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return '—'
  }
}

export function Logs() {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [qApplied, setQApplied] = useState('')
  const [level, setLevel] = useState<string>('')
  const [category, setCategory] = useState<string>('')
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('limit', '50')
  if (qApplied) params.set('q', qApplied)
  if (level) params.set('level', level)
  if (category) params.set('category', category)
  if (from) params.set('from', new Date(from).toISOString())
  if (to) params.set('to', new Date(to).toISOString())

  const { data, isLoading, isFetching, refetch } = useQuery<LogsData>({
    queryKey: ['system-logs', params.toString()],
    queryFn: () => apiGet(`/api/admin/logs?${params.toString()}`),
    placeholderData: (prev) => prev,
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setQApplied(q.trim())
  }

  function resetFilters() {
    setQ('')
    setQApplied('')
    setLevel('')
    setCategory('')
    setFrom('')
    setTo('')
    setPage(1)
  }

  return (
    <>
      <Topbar
        title="Logs del sistema"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} eventos registrados` : 'Cargando…'}
        actions={
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <RefreshCw className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Refrescar
          </button>
        }
      />
      <div className="p-6">
        <form
          onSubmit={onSearch}
          className="mb-4 grid gap-2 rounded-2xl border border-ink-200 bg-white p-3 md:grid-cols-12"
        >
          <div className="relative md:col-span-4">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar mensaje, usuario, acción…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <select
            value={level}
            onChange={(e) => {
              setLevel(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-2"
          >
            <option value="">Todos los niveles</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
          </select>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-2"
          >
            <option value="">Todas las categorías</option>
            {(data?.categories || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-2"
            title="Desde"
          />
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-2"
            title="Hasta"
          />
          <div className="flex gap-2 md:col-span-12">
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Filter className="size-3.5" /> Aplicar filtros
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              Limpiar
            </button>
          </div>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando logs…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Timestamp</th>
                      <th className="px-4 py-3 font-medium">Nivel</th>
                      <th className="px-4 py-3 font-medium">Categoría</th>
                      <th className="px-4 py-3 font-medium">Usuario</th>
                      <th className="px-4 py-3 font-medium">Acción</th>
                      <th className="px-4 py-3 font-medium">Entidad</th>
                      <th className="px-4 py-3 font-medium">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-sm text-ink-500">
                          Sin eventos
                        </td>
                      </tr>
                    )}
                    {data.data.map((r) => (
                      <Fragment key={r.id}>
                        <tr
                          className="cursor-pointer border-t border-ink-100 hover:bg-ink-50"
                          onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        >
                          <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-ink-700">
                            {fmtDate(r.ts)}
                          </td>
                          <td className="px-4 py-2">{levelBadge(r.level)}</td>
                          <td className="px-4 py-2">
                            <span className="rounded-md bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-700">
                              {r.category}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-ink-700">
                            {r.userEmail || '—'}
                            {r.ip && (
                              <span className="ml-1 text-ink-400">({r.ip})</span>
                            )}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-ink-700">{r.action}</td>
                          <td className="px-4 py-2 text-xs text-ink-700">
                            {r.entity ? (
                              <span>
                                {r.entity}
                                {r.entityId && <span className="text-ink-400">#{r.entityId}</span>}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="max-w-md truncate px-4 py-2 text-xs text-ink-700">
                            {r.message || '—'}
                          </td>
                        </tr>
                        {expanded === r.id && (
                          <tr className="border-t border-ink-100 bg-ink-50/50">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="space-y-2 text-xs">
                                {r.message && (
                                  <div>
                                    <span className="font-semibold text-ink-700">Mensaje:</span>{' '}
                                    <span className="text-ink-900">{r.message}</span>
                                  </div>
                                )}
                                {r.meta && (
                                  <div>
                                    <span className="font-semibold text-ink-700">Meta:</span>
                                    <pre className="mt-1 overflow-x-auto rounded-md bg-ink-900 px-3 py-2 font-mono text-[11px] leading-relaxed text-ink-100">
                                      {(() => {
                                        try {
                                          return JSON.stringify(JSON.parse(r.meta), null, 2)
                                        } catch {
                                          return r.meta
                                        }
                                      })()}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong> (
                  {data.total.toLocaleString('es-MX')} eventos)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Logs
